/**
 * Display formatting.
 *
 * Presentation only — never arithmetic. Anything that changes a number's value
 * belongs in `money.ts`, where it is mirrored by the backend and tested against a
 * shared fixture table.
 */

import type { Decimal } from "decimal.js";

import { money, type MoneyInput } from "./money";

/**
 * Format an amount as South African rand: `R 142.03`, `R 98,450.00`.
 *
 * Note the locale. `en-ZA` is *not* used, deliberately: several implementations
 * render it with a comma decimal separator and a space for thousands
 * (`R 98 450,00`), which is correct for formal South African typography but is
 * not what the supplied designs show and is not what a spaza till displays.
 * `en-US` grouping gives the comma-thousands, dot-decimal form in the screenshots.
 */
export function formatMoney(
  value: MoneyInput,
  options: { symbol?: boolean; sign?: boolean } = {},
): string {
  const { symbol = true, sign = false } = options;
  const amount = money(value);

  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount.toNumber()));

  const negative = amount.isNegative();
  const prefix = negative ? "-" : sign ? "+" : "";

  return symbol ? `${prefix}R ${formatted}` : `${prefix}${formatted}`;
}

/**
 * Format a quantity, trimming trailing zeros: `2`, `0.25`, `1.5`.
 *
 * Stock is stored to three decimals for weighed goods, but a shelf-stable count
 * of 48 should read `48`, not `48.000` — the noise makes a stock list harder to
 * scan for the number that is actually wrong.
 */
export function formatQuantity(value: MoneyInput | Decimal): string {
  const n = typeof value === "object" && "toFixed" in value ? value : money(value);
  return n.toDecimalPlaces(3).toString();
}

/** `48 Units`, `1 Unit` — the Inventory table's stock column. */
export function formatUnits(value: MoneyInput | Decimal, unit = "Unit"): string {
  const qty = formatQuantity(value);
  return `${qty} ${qty === "1" ? unit : `${unit}s`}`;
}

/** `+24 Units` / `−10 Units` — a stock movement's signed delta. */
export function formatMovement(value: MoneyInput | Decimal, unit = "Unit"): string {
  const n = typeof value === "object" && "toFixed" in value ? value : money(value);
  // U+2212 MINUS SIGN, not a hyphen: it aligns with digits and reads as
  // arithmetic rather than as punctuation.
  const prefix = n.isNegative() ? "−" : "+";
  return `${prefix}${formatUnits(n.abs(), unit)}`;
}

const SAST = "Africa/Johannesburg";

/** `08 May 2026` — the Inventory table's Last Updated date. */
export function formatDate(value: Date | string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: SAST,
  }).format(toDate(value));
}

/** `10:30 AM` — the time beneath it, and the Recent Sales column. */
export function formatTime(value: Date | string): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: SAST,
  }).format(toDate(value));
}

export function formatDateTime(value: Date | string): string {
  return `${formatDate(value)} ${formatTime(value)}`;
}

/**
 * `just now`, `3 min ago`, `14:32`.
 *
 * Used by the sync chip's "Last synced" label. Beyond an hour it switches to a
 * clock time, because "97 minutes ago" is harder to act on than "14:32" when a
 * merchant is deciding whether their morning's sales have uploaded.
 */
export function formatRelativeTime(value: Date | string | null | undefined): string {
  if (!value) return "never";

  const then = toDate(value);
  const seconds = Math.floor((Date.now() - then.getTime()) / 1000);

  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;

  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: SAST,
  }).format(then);
}

/** `INV-000124` → shown verbatim; here for symmetry with the backend helper. */
export function formatDocumentNumber(prefix: string, sequence: number, width = 6): string {
  return `${prefix}-${String(sequence).padStart(width, "0")}`;
}

/**
 * `1 sale`, `4 sales`.
 *
 * Trivial, and here rather than inline because "1 sales" on a till bar reads as
 * unfinished software — which is what a shopkeeper concludes about the rest of
 * it too.
 */
export function plural(count: number, noun: string, plural_ = `${noun}s`): string {
  return `${count} ${count === 1 ? noun : plural_}`;
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}
